import { createApp } from 'vue'
import App from './App.vue'
import router, { INDEX_ROUTES } from './router'
import vuetify from './plugins/vuetify'
import 'vuetify/dist/vuetify.min.css'
import * as Sentry from '@sentry/vue'
import { useApi } from '@/api/useApi'
import HeaderImage from '@/assets/header.jpg'
import HeaderImageLazy from '@/assets/header_lazy.jpg'
import { pinia } from '@/modules/pinia'
import apiUrls from '@/api/apiUrls'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'

import { useAuthenticationStore } from '@/stores/authentication'

pinia.use(({ store }) => {
	store.$api = api
	store.$apiUrls = apiUrls
})

const app = createApp(App).use(vuetify).use(pinia).use(router)
const api = useApi()

const authStore = useAuthenticationStore(pinia)
const { bus } = useEventsBus()

app
	.use(AuthenticationManagement, {
		appTitle: 'SHIP',
		headerImage: HeaderImage,
		headerImageLazy: HeaderImageLazy,
		authStore: authStore,
		apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL.replace(/\/$/, ''),
		useLegacyAuthentication: true,
		redirectRouterName: INDEX_ROUTES.SHIP.name
	})
	.use(ComponentLibrary, { externalBus: bus })

Sentry.init({
	app,
	dsn: import.meta.env.VITE_APP_SENTRY_DSN,
	environment: import.meta.env.MODE,
	tracePropagationTargets: ['*'],
	integrations: [
		Sentry.browserTracingIntegration({ router }),
		Sentry.replayIntegration(),
		Sentry.feedbackIntegration({
			triggerLabel: 'Feedback',
			formTitle: 'Send Feedback',
			submitButtonLabel: 'Send Feedback',
			cancelButtonLabel: 'Cancel',
			messagePlaceholder: 'What would you like to tell us?',
			successMessageText: 'Thank you for your feedback!',
			colorScheme: 'light',
			showName: true,
			showEmail: true,
			enableScreenshot: true,
			autoInject: false
		})
	],
	// Performance Monitoring
	tracesSampleRate: 0.1, // Capture 10% of the transactions
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.mount('#app')

router.isReady().then(() => {
	const feedback = Sentry.getFeedback()
	if (!feedback) return

	let widget: ReturnType<typeof feedback.createWidget> | null = null

	const isAuthenticated = () => {
		return authStore.user !== null
	}

	const updateFeedbackWidget = () => {
		if (isAuthenticated() && !widget) {
			widget = feedback.createWidget()
			widget.appendToDom()
			if (authStore.user?.email) {
				Sentry.setUser({
					username: authStore.user.first_name ?? '',
					email: authStore.user.email ?? ''
				})
			}
		} else if (!isAuthenticated() && widget) {
			widget.removeFromDom()
			widget = null
			Sentry.setUser(null)
		}
	}
	updateFeedbackWidget()
	authStore.$subscribe(updateFeedbackWidget)
})

export default app
